import type { ItemFila } from './useFilaDoDia';

/**
 * Risco de exclusão/cancelamento da cota, na Fila de hoje.
 *
 * QUEM DECIDE É O BANCO. `seller_action_queue` (migrations 219 e 222) entrega, só nas
 * linhas de cobrança, `risco` ('alto' | 'medio') e `parcelas_ate_exclusao`.
 * As regras são SIMPLES de propósito (decisão do Rick: regra que o vendedor
 * entende, não modelo medido):
 *
 *   alto  = não contemplado com 3+ parcelas em aberto (a caminho das 7 da
 *           Cl. 43ª, que excluem do grupo)
 *         | 2+ parcelas em aberto com até 3 pagas (começo do plano: o cliente
 *           ainda não criou vínculo com a cota)
 *         | contemplado com 2+ em aberto (vencimento antecipado, Cl. 46ª)
 *   medio = o resto dos inadimplentes
 *
 *   parcelas_ate_exclusao = 7 - em aberto (mínimo 0), só p/ NÃO contemplado.
 *
 * Aqui só se ORDENA e se ESCREVE o que a view já decidiu — recalcular a regra
 * no cliente seria ter duas versões dela para divergir.
 */

export type RiscoFila = 'alto' | 'medio';

/** Cl. 43ª do contrato: prestações consecutivas em aberto que excluem do grupo.
 *  Só para texto; quem faz a conta é a view. */
export const PARCELAS_PARA_EXCLUSAO = 7;

/** Explicação curta da regra, para o `title` do selo. */
export const EXPLICACAO_RISCO =
  'Risco alto pelas regras da fila: 3 ou mais parcelas em aberto (2 ou mais se ' +
  'já contemplado, ou no começo do plano, com até 3 parcelas pagas). Pelo contrato, ' +
  `${PARCELAS_PARA_EXCLUSAO} parcelas consecutivas em aberto excluem do grupo ` +
  'quem não foi contemplado; o contemplado com atraso tem as parcelas restantes antecipadas.';

type ComRisco = Pick<ItemFila, 'risco' | 'parcelas_ate_exclusao'>;

/** 0 = mais urgente. Médio e "sem risco" (outros tipos) empatam: só o alto sobe. */
const pesoDoRisco = (r: ItemFila['risco']) => (r === 'alto' ? 0 : 1);

/**
 * Ordena a fila: prioridade primeiro, e dentro dela o risco alto na frente.
 * Todo o resto mantém a ordem da consulta (dias/parcelas esperando, do maior
 * para o menor) — o `sort` do JS é estável, então o desempate é o do banco.
 *
 * Só o ALTO sobe: médio fica misturado com as assembleias como sempre esteve,
 * para a mudança não embaralhar a fila de quem não tem risco nenhum.
 */
export const ordenarPorRisco = <T extends Pick<ItemFila, 'priority' | 'risco'>>(
  linhas: T[],
): T[] =>
  [...linhas].sort(
    (a, b) => a.priority - b.priority || pesoDoRisco(a.risco) - pesoDoRisco(b.risco),
  );

/**
 * Ao juntar os contratos da mesma pessoa numa linha, o risco da linha é o do
 * contrato mais arriscado — é ele que torna a ligação urgente. Mesmo risco:
 * vale o que está mais perto da exclusão (menor número de parcelas restantes).
 */
export const juntarRisco = (atual: ComRisco, novo: ComRisco): ComRisco => {
  const pa = pesoDoRisco(atual.risco);
  const pn = pesoDoRisco(novo.risco);
  if (pn < pa || (atual.risco == null && novo.risco != null)) {
    return { risco: novo.risco, parcelas_ate_exclusao: novo.parcelas_ate_exclusao };
  }
  if (pn > pa) return atual;
  const a = atual.parcelas_ate_exclusao;
  const n = novo.parcelas_ate_exclusao;
  return {
    risco: atual.risco,
    parcelas_ate_exclusao: a == null ? n ?? null : n == null ? a : Math.min(a, n),
  };
};

/**
 * O que a linha diz além do selo. Só para risco ALTO de cobrança — no médio,
 * o selo "atraso" e o "1 parcela em aberto" já dizem tudo.
 *
 * `parcelas_ate_exclusao` nulo numa cobrança = cota contemplada (a exclusão
 * não se aplica; o risco é o vencimento antecipado).
 */
export const textoDoRisco = (
  i: Pick<ItemFila, 'kind' | 'risco' | 'parcelas_ate_exclusao'>,
): string | null => {
  if (i.kind !== 'cobranca_inadimplente' || i.risco !== 'alto') return null;
  const n = i.parcelas_ate_exclusao;
  if (n == null) return 'contemplado: risco de vencimento antecipado';
  if (n <= 0) return 'no limite da exclusão pelo contrato';
  if (n === 1) return 'falta 1 parcela para a exclusão';
  return `faltam ${n} parcelas para a exclusão`;
};

/** Selo "Risco alto" ao lado do nome; nulo quando não há o que destacar. */
export const seloDoRisco = (
  i: Pick<ItemFila, 'kind' | 'risco'>,
): { texto: string; explicacao: string } | null =>
  i.kind === 'cobranca_inadimplente' && i.risco === 'alto'
    ? { texto: 'Risco alto', explicacao: EXPLICACAO_RISCO }
    : null;
